import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Trash2, LogOut, Save, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { API_BASE } from '@/config/api';
import { DEFAULT_RECORRIDO_ID, RECORRIDOS_BY_ID } from '@/data/recorridos/config';
import { paraderoPath } from '@/lib/paraderoRoutes';
import { clearAdminToken, getAdminToken } from '@/lib/adminAuth';

function authHeaders() {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function AdminPanel() {
  const token = getAdminToken();
  const [tab, setTab] = useState('alertas');

  const [newUserUsername, setNewUserUsername] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserConfirm, setNewUserConfirm] = useState('');
  const [userSaving, setUserSaving] = useState(false);
  const [userMsg, setUserMsg] = useState(null);

  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [alertsError, setAlertsError] = useState(null);

  const [newTitle, setNewTitle] = useState('Aviso');
  const [newBody, setNewBody] = useState('');
  const [newPriority, setNewPriority] = useState(0);
  const [newActive, setNewActive] = useState(true);

  /** Una fila por recorrido (t1…t5): patente asignada al GPS de ese trayecto. */
  const [assignRows, setAssignRows] = useState([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null);
  const [activoSavingId, setActivoSavingId] = useState(null);
  const [gpsPatentes, setGpsPatentes] = useState([]);

  const assignRowsRef = useRef(assignRows);
  const autosaveTimerRef = useRef(null);

  const canUse = Boolean(API_BASE && token);

  useEffect(() => {
    assignRowsRef.current = assignRows;
  }, [assignRows]);

  useEffect(
    () => () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    },
    []
  );

  const loadAlerts = useCallback(async () => {
    if (!canUse) return;
    setAlertsError(null);
    setAlertsLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE}/admin/alertas`, { headers: authHeaders() });
      setAlerts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.response?.status === 401) {
        clearAdminToken();
      }
      setAlertsError('No se pudieron cargar las alertas.');
    } finally {
      setAlertsLoading(false);
    }
  }, [canUse]);

  const loadCatalog = useCallback(async () => {
    try {
      const [tRes, aRes, gpsRes] = await Promise.all([
        axios.get(`${API_BASE}/trayectos`),
        axios.get(`${API_BASE}/asignaciones/buses`),
        axios.get(`${API_BASE}/gps/link-sur`).catch(() => ({ data: {} })),
      ]);
      const tList = Array.isArray(tRes.data) ? tRes.data : [];
      const byTray = {};
      const raw = aRes.data?.assignments;
      if (Array.isArray(raw)) {
        raw.forEach((r) => {
          if (r.trayectoId && r.patente) byTray[r.trayectoId] = (r.patente || '').trim();
        });
      }
      setAssignRows(
        tList.map((t) => {
          const cfg = RECORRIDOS_BY_ID[t.id];
          const activo =
            typeof t.activo === 'boolean'
              ? t.activo
              : cfg?.activo !== false;
          return {
            trayectoId: t.id,
            codigo: t.codigo,
            nombre: t.nombre,
            patente: byTray[t.id] || '',
            activo,
          };
        })
      );
      const p = gpsRes.data?.patentes;
      setGpsPatentes(Array.isArray(p) ? p : []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  const patchTrayectoActivo = useCallback(
    async (trayectoId, activo) => {
      if (!canUse) return;
      setActivoSavingId(trayectoId);
      setAssignMsg(null);
      try {
        await axios.patch(
          `${API_BASE}/admin/trayectos/${encodeURIComponent(trayectoId)}/activo`,
          { activo },
          { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
        );
        setAssignRows((rows) =>
          rows.map((r) => (r.trayectoId === trayectoId ? { ...r, activo } : r))
        );
        try {
          const gpsRes = await axios.get(`${API_BASE}/gps/link-sur`).catch(() => ({ data: {} }));
          const p = gpsRes.data?.patentes;
          setGpsPatentes(Array.isArray(p) ? p : []);
        } catch {
          /* ignore */
        }
        setAssignMsg('Estado del recorrido actualizado.');
      } catch (err) {
        if (err.response?.status === 401) clearAdminToken();
        setAssignMsg('No se pudo actualizar la visibilidad del recorrido.');
      } finally {
        setActivoSavingId(null);
      }
    },
    [canUse]
  );

  const createUser = async (e) => {
    e.preventDefault();
    setUserMsg(null);
    if (newUserPassword !== newUserConfirm) {
      setUserMsg({ ok: false, text: 'Las contraseñas no coinciden.' });
      return;
    }
    setUserSaving(true);
    try {
      await axios.post(
        `${API_BASE}/admin/usuarios`,
        { username: newUserUsername, password: newUserPassword },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      setUserMsg({ ok: true, text: `Usuario «${newUserUsername}» creado correctamente.` });
      setNewUserUsername('');
      setNewUserPassword('');
      setNewUserConfirm('');
    } catch (err) {
      if (err.response?.status === 401) clearAdminToken();
      const detail = err.response?.data?.detail;
      setUserMsg({ ok: false, text: detail || 'No se pudo crear el usuario.' });
    } finally {
      setUserSaving(false);
    }
  };

  const logout = () => {
    clearAdminToken();
    window.location.href = '/admin/login';
  };

  const createAlert = async (e) => {
    e.preventDefault();
    if (!canUse) return;
    setAlertsError(null);
    try {
      await axios.post(
        `${API_BASE}/admin/alertas`,
        {
          title: newTitle,
          body: newBody,
          priority: Number(newPriority) || 0,
          active: newActive,
        },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
      );
      setNewBody('');
      setNewTitle('Aviso');
      setNewPriority(0);
      await loadAlerts();
    } catch (err) {
      if (err.response?.status === 401) clearAdminToken();
      setAlertsError('No se pudo crear la alerta.');
    }
  };

  const patchAlert = async (id, patch) => {
    if (!canUse) return;
    try {
      await axios.patch(`${API_BASE}/admin/alertas/${id}`, patch, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      });
      await loadAlerts();
    } catch (err) {
      if (err.response?.status === 401) clearAdminToken();
    }
  };

  const deleteAlert = async (id) => {
    if (!canUse || !window.confirm('¿Eliminar esta alerta?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/alertas/${id}`, { headers: authHeaders() });
      await loadAlerts();
    } catch (err) {
      if (err.response?.status === 401) clearAdminToken();
    }
  };

  const performSaveAssignments = useCallback(
    async ({ silent = false } = {}) => {
      if (!canUse) return;
      const rows = assignRowsRef.current;
      const assignments = rows
        .filter((r) => r.activo !== false)
        .map((r) => ({
          patente: (r.patente || '').trim(),
          trayectoId: (r.trayectoId || '').trim().toLowerCase(),
        }))
        .filter((r) => r.patente && r.trayectoId);
      if (!silent) {
        setAssignSaving(true);
        setAssignMsg(null);
      }
      try {
        await axios.put(
          `${API_BASE}/admin/asignaciones/buses`,
          { assignments },
          { headers: { ...authHeaders(), 'Content-Type': 'application/json' } }
        );
        if (silent) {
          setAssignMsg('Guardado automáticamente.');
        } else {
          setAssignMsg('Asignaciones guardadas.');
        }
      } catch (err) {
        if (err.response?.status === 401) clearAdminToken();
        setAssignMsg(
          silent
            ? 'No se pudo guardar automáticamente. Revisá la conexión o guardá manualmente.'
            : 'No se pudo guardar. Revisa patente y trayecto.'
        );
      } finally {
        if (!silent) setAssignSaving(false);
      }
    },
    [canUse]
  );

  const performSaveAssignmentsRef = useRef(performSaveAssignments);
  performSaveAssignmentsRef.current = performSaveAssignments;

  const scheduleAutosavePatentes = useCallback(() => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      performSaveAssignmentsRef.current?.({ silent: true });
    }, 1100);
  }, []);

  const fillFirstEmptyPatente = useCallback(
    (patente) => {
      const p = (patente || '').trim();
      if (!p) return;
      setAssignRows((rows) => {
        const i = rows.findIndex((r) => r.activo !== false && !(r.patente || '').trim());
        if (i < 0) return rows;
        const next = [...rows];
        next[i] = { ...next[i], patente: p };
        assignRowsRef.current = next;
        return next;
      });
      scheduleAutosavePatentes();
    },
    [scheduleAutosavePatentes]
  );

  const saveAssignments = async () => {
    if (!canUse) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    await performSaveAssignments({ silent: false });
  };

  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-headline text-2xl font-extrabold tracking-tight">Panel</h1>
          <p className="text-sm text-muted-foreground">Alertas de viaje y buses por trayecto</p>
        </div>
        <Button type="button" variant="outline" className="touch-target gap-2" onClick={logout}>
          <LogOut className="h-4 w-4" />
          Salir
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3">
          <TabsTrigger value="alertas">Alertas de viaje</TabsTrigger>
          <TabsTrigger value="buses">Buses y trayectos</TabsTrigger>
          <TabsTrigger value="usuarios">Usuarios</TabsTrigger>
        </TabsList>

        <TabsContent value="alertas" className="space-y-6 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Nueva alerta</CardTitle>
              <CardDescription>Se mostrará en la vista paradero, sección «Alertas de viaje».</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createAlert} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="na-title">Título</Label>
                  <Input
                    id="na-title"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    maxLength={200}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="na-body">Mensaje</Label>
                  <Textarea
                    id="na-body"
                    value={newBody}
                    onChange={(e) => setNewBody(e.target.value)}
                    rows={4}
                    maxLength={4000}
                  />
                </div>
                <div className="flex flex-wrap items-center gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="na-prio">Prioridad (mayor = arriba)</Label>
                    <Input
                      id="na-prio"
                      type="number"
                      value={newPriority}
                      onChange={(e) => setNewPriority(e.target.value)}
                      className="w-28"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch id="na-act" checked={newActive} onCheckedChange={setNewActive} />
                    <Label htmlFor="na-act">Visible</Label>
                  </div>
                </div>
                <Button type="submit">Publicar alerta</Button>
              </form>
            </CardContent>
          </Card>

          <div>
            <h2 className="mb-3 text-sm font-extrabold uppercase tracking-widest text-muted-foreground">
              Alertas publicadas
            </h2>
            {alertsLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
            {alertsError && <p className="text-sm text-destructive">{alertsError}</p>}
            {!alertsLoading && alerts.length === 0 && (
              <p className="text-sm text-muted-foreground">No hay alertas todavía.</p>
            )}
            <ul className="space-y-3">
              {alerts.map((a) => (
                <li key={a.id}>
                  <Card className="border-border/60">
                    <CardContent className="space-y-3 pt-6">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <p className="font-bold">{a.title}</p>
                          <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">{a.body}</p>
                          <p className="mt-2 text-[10px] text-muted-foreground">
                            {a.createdAt &&
                              formatDistanceToNow(new Date(a.createdAt), { addSuffix: true, locale: es })}
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => patchAlert(a.id, { active: !a.active })}
                          >
                            {a.active ? 'Ocultar' : 'Mostrar'}
                          </Button>
                          <Button type="button" variant="destructive" size="sm" onClick={() => deleteAlert(a.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>

        <TabsContent value="buses" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Patente por trayecto</CardTitle>
              <CardDescription>
                <strong>Interruptor</strong>: guarda en base de datos si el recorrido se muestra en landing y chips
                (la app lee <code className="rounded bg-muted px-1 text-xs">GET /api/trayectos</code>). Las
                patentes de recorridos visibles se guardan solas unos segundos después de escribir; podés usar
                &quot;Guardar asignaciones&quot; para forzar. Los inactivos no admiten asignación de patente en
                esta pantalla. El endpoint{' '}
                <code className="rounded bg-muted px-1 text-xs">GET /api/trayectos/por-patente/&#123;patente&#125;</code>{' '}
                resuelve trayecto para integraciones.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {gpsPatentes.length > 0 && (
                <div className="rounded-lg border border-border/60 bg-muted/30 p-3">
                  <p className="mb-2 text-xs font-medium text-muted-foreground">
                    Patentes vistas ahora por GPS (clic rellena la primera fila vacía de un recorrido activo)
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {gpsPatentes.map((p) => (
                      <Button
                        key={p}
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => fillFirstEmptyPatente(p)}
                      >
                        {p}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <div className="hidden gap-3 border-b border-border/60 pb-2 text-[11px] font-bold uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-[minmax(0,1.2fr)_100px_minmax(0,1fr)]">
                <span>Recorrido</span>
                <span>Estado</span>
                <span>Patente</span>
              </div>
              {assignRows.map((row, idx) => (
                <div
                  key={row.trayectoId}
                  className="flex flex-col gap-3 border-b border-border/40 pb-4 sm:grid sm:grid-cols-[minmax(0,1.2fr)_100px_minmax(0,1fr)] sm:items-end"
                >
                  <div className="min-w-0 space-y-2">
                    <Label className="sm:hidden">Recorrido</Label>
                    <p className="text-sm font-semibold leading-tight">
                      {row.codigo}
                      <span className="block text-xs font-normal text-muted-foreground">{row.nombre}</span>
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="sm:hidden">Estado</Label>
                    <div className="flex flex-col gap-1 sm:max-w-[140px]">
                      <div className="flex items-center gap-2">
                        <Switch
                          id={`act-${row.trayectoId}`}
                          checked={row.activo !== false}
                          disabled={activoSavingId === row.trayectoId}
                          onCheckedChange={(checked) => patchTrayectoActivo(row.trayectoId, checked)}
                        />
                        <Label htmlFor={`act-${row.trayectoId}`} className="cursor-pointer text-xs font-medium">
                          {row.activo !== false ? 'Visible' : 'Oculto'}
                        </Label>
                      </div>
                    </div>
                  </div>
                  <div className="min-w-[160px] space-y-2">
                    {row.activo === false ? (
                      <>
                        <Label className="max-sm:inline-block sm:sr-only">Patente</Label>
                        <p className="text-sm text-muted-foreground">
                          Sin asignación en la app (reemplazo externo). No se envía al GPS público.
                        </p>
                      </>
                    ) : (
                      <>
                        <Label
                          htmlFor={`pat-${row.trayectoId}`}
                          className="max-sm:inline-block sm:sr-only"
                        >
                          Patente asignada
                        </Label>
                        <Input
                          id={`pat-${row.trayectoId}`}
                          placeholder="Ej. SDWY 44"
                          value={row.patente}
                          onChange={(e) => {
                            const next = [...assignRows];
                            next[idx] = { ...next[idx], patente: e.target.value };
                            assignRowsRef.current = next;
                            setAssignRows(next);
                            scheduleAutosavePatentes();
                          }}
                          autoComplete="off"
                        />
                      </>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={saveAssignments} disabled={assignSaving} className="gap-2">
                  <Save className="h-4 w-4" />
                  {assignSaving ? 'Guardando…' : 'Guardar asignaciones'}
                </Button>
              </div>
              {assignMsg && <p className="text-sm text-muted-foreground">{assignMsg}</p>}
              <p className="text-xs text-muted-foreground">
                <Link
                  to={paraderoPath({
                    recorridoId: DEFAULT_RECORRIDO_ID,
                  })}
                  className="font-semibold text-primary underline"
                >
                  Ver vista recorrido en vivo
                </Link>{' '}
                para comprobar el filtro de bus según la patente activa.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="usuarios" className="space-y-6 pt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Crear usuario admin</CardTitle>
              <CardDescription>
                El nuevo usuario podrá iniciar sesión en este panel. Usá un email o un nombre corto.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createUser} className="space-y-4 max-w-sm">
                <div className="space-y-2">
                  <Label htmlFor="nu-user">Usuario / email</Label>
                  <Input
                    id="nu-user"
                    type="text"
                    placeholder="ej. inspectortransito@paine.cl"
                    value={newUserUsername}
                    onChange={(e) => setNewUserUsername(e.target.value)}
                    autoComplete="off"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nu-pwd">Contraseña (mín. 8 caracteres)</Label>
                  <Input
                    id="nu-pwd"
                    type="password"
                    value={newUserPassword}
                    onChange={(e) => setNewUserPassword(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="nu-confirm">Confirmar contraseña</Label>
                  <Input
                    id="nu-confirm"
                    type="password"
                    value={newUserConfirm}
                    onChange={(e) => setNewUserConfirm(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>
                {userMsg && (
                  <p className={`text-sm ${userMsg.ok ? 'text-green-600' : 'text-destructive'}`}>
                    {userMsg.text}
                  </p>
                )}
                <Button type="submit" disabled={userSaving} className="gap-2">
                  <UserPlus className="h-4 w-4" />
                  {userSaving ? 'Creando…' : 'Crear usuario'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
